import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  FileDown,
  Loader2,
  Mail,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { publicUrl } from '@/lib/publicSite';
import { QRCodeCanvas } from 'qrcode.react';
import {
  CONSENT_ITEMS,
  HEALTH_QUESTIONS,
  PHOTO_CONSENT_LABEL,
  YES_NO_LABEL,
  type PhotoConsent,
  type YesNo,
} from '@/lib/healthForm';

type Row = {
  id: string;
  patient_name: string;
  patient_email: string | null;
  patient_phone: string | null;
  status: 'sent' | 'filled';
  treatment_types: string[] | null;
  photo_consent: string | null;
  answers: Record<string, any> | null;
  sent_at: string | null;
  filled_at: string | null;
  emailed_at: string | null;
  email_error: string | null;
  expires_at: string;
  created_at: string;
  pdf_path: string | null;
};

async function callAdmin(payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('health-forms-admin', { body: payload });
  if (error) {
    let detail = error.message;
    try {
      const ctx = (error as any).context;
      if (ctx?.text) detail = (await ctx.text()) || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  if ((data as any)?.error) throw new Error((data as any).error);
  return data as any;
}

export default function AdminHealthForms() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [linkDialog, setLinkDialog] = useState<string | null>(null);

  const openLink = publicUrl('/health-form/new');

  const downloadQr = () => {
    const canvas = document.getElementById('health-form-qr') as HTMLCanvasElement | null;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'riham-health-form-qr.png';
    a.click();
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await callAdmin({ action: 'list' });
      setRows(data.rows ?? []);
      setEmailConfigured(data.emailConfigured !== false);
    } catch (e) {
      toast.error('שגיאה בטעינת הטפסים', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.patient_name?.toLowerCase().includes(q) ||
        (r.patient_email ?? '').toLowerCase().includes(q) ||
        (r.patient_phone ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const copyLink = async (link: string) => {
    setLinkDialog(link);
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.setAttribute('readonly', 'true');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        copied = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        copied = false;
      }
    }
    if (copied) toast.success('הקישור הועתק');
    else toast.message('נא להעתיק את הקישור מהחלון');
  };

  const create = async (send: boolean) => {
    if (form.name.trim().length < 2) {
      toast.error('נא למלא שם מלא');
      return;
    }
    setCreating(true);
    try {
      const data = await callAdmin({ action: 'create', ...form, send });
      if (send) {
        if (data.emailError) toast.error('הקישור נוצר אך המייל לא נשלח', { description: data.emailError });
        else toast.success('הקישור נשלח במייל');
      } else {
        await copyLink(data.link);
      }
      setForm({ name: '', email: '', phone: '' });
      load();
    } catch (e) {
      toast.error('שגיאה', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setCreating(false);
    }
  };

  const reissue = async (id: string, send: boolean) => {
    setBusyId(id);
    try {
      const data = await callAdmin({ action: 'reissue', id, send });
      if (send) {
        if (data.emailError) toast.error('המייל לא נשלח', { description: data.emailError });
        else toast.success('קישור חדש נשלח במייל');
      } else {
        await copyLink(data.link);
      }
      load();
    } catch (e) {
      toast.error('שגיאה', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusyId(null);
    }
  };

  const openPdf = async (id: string) => {
    setBusyId(id);
    try {
      const data = await callAdmin({ action: 'pdfUrl', id });
      window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error('שגיאה בפתיחת המסמך', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('למחוק את הטופס והמסמך לצמיתות?')) return;
    setBusyId(id);
    try {
      await callAdmin({ action: 'delete', id });
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success('נמחק');
    } catch (e) {
      toast.error('שגיאה במחיקה', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div dir="rtl" className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">הצהרת בריאות והסכמה לטיפול</h1>
        <p className="text-sm text-muted-foreground">
          שליחת טופס דיגיטלי לחתימה מהנייד. עותק PDF חתום נשלח למטופלת ולקליניקה.
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <h2 className="font-semibold flex items-center gap-2">
          <Plus className="h-4 w-4" /> טופס חדש
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">שם מלא *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">אימייל</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">טלפון</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button disabled={creating || !form.email} onClick={() => create(true)}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Mail className="h-4 w-4 me-1" />}
            שליחה במייל
          </Button>
          <Button variant="outline" disabled={creating} onClick={() => create(false)}>
            <Copy className="h-4 w-4 me-1" /> יצירה והעתקת קישור
          </Button>
        </div>
        {!emailConfigured && (
          <p className="text-xs text-amber-700">שליחת מייל אינה מוגדרת — ניתן ליצור קישור ולהעתיק אותו.</p>
        )}
      </Card>

      <Card className="p-5 space-y-4">
        <h2 className="font-semibold flex items-center gap-2">
          <QrCode className="h-4 w-4" /> טופס ריק לקליניקה (קישור ציבורי / QR)
        </h2>
        <p className="text-sm text-muted-foreground">
          מטופלת שסורקת את הקוד או פותחת את הקישור ממלאת את הטופס מאפס — ללא שליחה מוקדמת. העותק החתום נשלח
          אליה ולקליניקה, והטופס יופיע ברשימה למטה.
        </p>
        <div className="flex flex-wrap items-center gap-5">
          <div className="rounded-lg border bg-white p-3">
            <QRCodeCanvas id="health-form-qr" value={openLink} size={148} includeMargin level="M" />
          </div>
          <div className="flex-1 min-w-[240px] space-y-3">
            <Input readOnly value={openLink} onFocus={(e) => e.currentTarget.select()} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => copyLink(openLink)}>
                <Copy className="h-4 w-4 me-1" /> העתקת הקישור
              </Button>
              <Button variant="outline" size="sm" onClick={downloadQr}>
                <FileDown className="h-4 w-4 me-1" /> הורדת קוד QR
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(openLink, '_blank', 'noopener')}>
                פתיחת הטופס
              </Button>
            </div>
          </div>
        </div>
      </Card>


      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש לפי שם, טלפון, אימייל…"
            className="pr-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 me-1" /> רענון
        </Button>
        <span className="text-sm text-muted-foreground">{filtered.length} טפסים</span>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">טוען…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">אין טפסים להצגה</Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const expanded = expandedId === r.id;
            const a = r.answers ?? {};
            return (
              <Card key={r.id} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : r.id)}
                  className="w-full text-right p-4 flex items-center gap-4 hover:bg-neutral-50"
                >
                  <Badge
                    variant="outline"
                    className={`shrink-0 ${
                      r.status === 'filled'
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : 'bg-amber-100 text-amber-700 border-amber-200'
                    }`}
                  >
                    {r.status === 'filled' ? 'מולא וחתום' : 'נשלח / ממתין'}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.patient_name}</div>
                    <div className="text-xs text-muted-foreground truncate flex gap-3">
                      <span>{r.patient_phone}</span>
                      <span>{r.patient_email}</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {new Date(r.created_at).toLocaleDateString('he-IL')}
                  </div>
                  {expanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {expanded && (
                  <div className="border-t bg-neutral-50/50 p-5 space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {r.status === 'filled' && r.pdf_path && (
                        <Button size="sm" disabled={busyId === r.id} onClick={() => openPdf(r.id)}>
                          <FileDown className="h-4 w-4 me-1" /> פתיחת PDF חתום
                        </Button>
                      )}
                      {r.status === 'sent' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === r.id || !r.patient_email}
                            onClick={() => reissue(r.id, true)}
                          >
                            <Mail className="h-4 w-4 me-1" /> שליחה חוזרת
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === r.id}
                            onClick={() => reissue(r.id, false)}
                          >
                            <Copy className="h-4 w-4 me-1" /> קישור חדש להעתקה
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        disabled={busyId === r.id}
                        onClick={() => remove(r.id)}
                      >
                        <Trash2 className="h-4 w-4 me-1" /> מחיקה
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <div className="text-muted-foreground">נשלח</div>
                        <div>{r.sent_at ? new Date(r.sent_at).toLocaleString('he-IL') : '—'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">מולא</div>
                        <div>{r.filled_at ? new Date(r.filled_at).toLocaleString('he-IL') : '—'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">עותק במייל</div>
                        <div>{r.emailed_at ? 'נשלח' : r.email_error ?? '—'}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">תוקף הקישור</div>
                        <div>{new Date(r.expires_at).toLocaleDateString('he-IL')}</div>
                      </div>
                    </div>

                    {r.status === 'filled' && (
                      <Card className="p-4 bg-white text-sm space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                          <div>מספר זהות: <strong>{a.id_number || '—'}</strong></div>
                          <div>תאריך לידה: <strong>{a.birth_date || '—'}</strong></div>
                          <div>כתובת: <strong>{a.address || '—'}</strong></div>
                          <div>
                            סוג הטיפול: <strong>{(r.treatment_types ?? []).join(', ') || '—'}</strong>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">שאלון בריאות</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                            {HEALTH_QUESTIONS.map((q) => {
                              const v = a.questions?.[q.key] as YesNo | undefined;
                              return (
                                <div key={q.key} className="flex justify-between gap-3 border-b py-1">
                                  <span>{q.label}</span>
                                  <strong className={v === 'yes' ? 'text-red-600' : ''}>
                                    {v ? YES_NO_LABEL[v] : '—'}
                                  </strong>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div>תרופות: <strong>{a.medications || '—'}</strong></div>
                          <div>מצב העור: <strong>{a.skin_condition || '—'}</strong></div>
                          <div>אלרגיות: <strong>{a.allergies || '—'}</strong></div>
                          <div>טיפולים קודמים: <strong>{a.previous_treatments || '—'}</strong></div>
                          <div>הערות: <strong>{a.notes || '—'}</strong></div>
                          <div>
                            אישור צילום:{' '}
                            <strong>
                              {r.photo_consent
                                ? PHOTO_CONSENT_LABEL[r.photo_consent as PhotoConsent]
                                : '—'}
                            </strong>
                          </div>
                          <div className="text-xs text-muted-foreground pt-1">
                            אושרו {CONSENT_ITEMS.length} סעיפי הצהרה והסכמה, כולל חתימה דיגיטלית.
                          </div>
                        </div>
                      </Card>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!linkDialog} onOpenChange={(open) => !open && setLinkDialog(null)}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-right">קישור אישי לטופס</DialogTitle>
            <DialogDescription className="text-right">
              ניתן לשלוח את הקישור למטופלת בוואטסאפ או במייל. הקישור אישי וחד־פעמי.
            </DialogDescription>
          </DialogHeader>
          <Input
            readOnly
            dir="ltr"
            value={linkDialog ?? ''}
            onFocus={(e) => e.currentTarget.select()}
            className="text-xs"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => linkDialog && copyLink(linkDialog)}>
              <Copy className="h-4 w-4 me-1" /> העתקה
            </Button>
            {linkDialog && (
              <Button size="sm" variant="outline" asChild>
                <a href={linkDialog} target="_blank" rel="noopener noreferrer">
                  פתיחת הטופס
                </a>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
